import React from 'react';
import { EventItemFragment } from 'shared/__generated__/graphql';
import { format } from 'date-fns';
import { TWLink } from '../../../components/link';
import { cn } from '../../../utils/cn';
import { GoComment, GoCommentDiscussion } from 'react-icons/go';
import BasicEditor from '../rich-text/basic/basic-editor';
import DateTimeDisplay from 'web2/app/routes/event/-date-time-display';
import { PiMapPinLine } from 'react-icons/pi';
type EventItemProps = {
  event: EventItemFragment;
  size?: 'headline' | 'sm' | 'thumbnail';
  priority?: boolean;
  hideBottom?: boolean;
  bottomClasses?: string;
  isFeedItem?: boolean;
};

export default function EventItem({
  event,
  size = 'sm',
  priority = false,
  hideBottom = false,
  bottomClasses = '',
  isFeedItem = false,
}: EventItemProps) {
  const {
    id,
    title,
    imageUrl,
    date,
    endDate,
    location,
    attendeesFriendsCount,
    attendeesTotalCount,
    description,
    descriptionLexical,
    slug,
    commentsCount,
  } = event;
  const useSlug = !!(slug && !(event.settings?.unlistEvent || event.settings?.inviteOnly));
  const href = `/event/${useSlug ? slug : id}`;

  const formattedDate = date ? format(new Date(date), 'MMMM d, yyyy • h:mm a') : '';

  const isHeadline = size === 'headline';
  const isThumbnail = size === 'thumbnail';

  const titleSize = isHeadline
    ? 'typography-heading-lg text-[28px] md:text-[32px]'
    : isThumbnail
    ? 'typography-heading-sm text-[18px] md:text-[22px]'
    : 'typography-heading-md text-[22px] md:text-[26px]';

  const CommentsIcon = commentsCount > 0 ? GoCommentDiscussion : GoComment;

  if (isFeedItem) {
    return (
      <a
        href={href}
        className="text-center relative border border-brand-main border-dashed flex flex-col gap-[16px] w-[100%] bg-brand-background group"
      >
        <div className="relative w-full overflow-hidden aspect-[4/5]">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={title}
              className="w-full h-full object-cover object-center border-b border-brand-main border-dashed"
              loading={priority ? 'eager' : 'lazy'}
            />
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
              <span className="text-gray-400">No Image</span>
            </div>
          )}
        </div>
        <div className="flex flex-col px-4 flex-grow justify-between">
          <h2
            className={cn(
              'line-clamp-2 group-hover:text-brand-highlight',
              'typography-heading-sm text-[24px] md:text-[32px]',
              bottomClasses,
            )}
          >
            <TWLink to={href}>{title}</TWLink>
          </h2>

          {date && (
            <DateTimeDisplay
              className="mt-2"
              start={date}
              end={endDate}
              dateClassName="text-brand-main text-[14px] leading-[14px]"
              timeClassName="text-brand-main text-[14px] leading-[20px]"
            />
          )}

          {descriptionLexical ? (
            <div className="mt-2">
              <BasicEditor
                initialEditorState={descriptionLexical}
                isReadOnly={true}
                withBorder={false}
                noOfLines={2}
                contentEditableProps={{
                  className: 'text-brand-main text-[16px] !p-0 w-full !border-none line-clamp-2',
                }}
                containerProps={{
                  className: 'text-brand-main !py-0 !pr-0',
                }}
              />
            </div>
          ) : (
            description && (
              <div className="typography-action-sm text-brand-main text-[16px] line-clamp-1 mt-2">
                {description}
              </div>
            )
          )}
        </div>

        <div>
          {location && (
            <div className="flex justify-center gap-1 text-brand-main typography-action-sm mt-2 text-[13.5px]">
              <PiMapPinLine className="size-4 text-brand-main" />
              <span className="truncate">{location}</span>
            </div>
          )}
        </div>

        {(attendeesFriendsCount > 0 || attendeesTotalCount > 0) && (
          <div className="flex justify-center">
            <div className="text-brand-highlight underline typography-action-md font-bold">
              {attendeesFriendsCount > 0
                ? `${attendeesFriendsCount} ${
                    attendeesFriendsCount === 1 ? 'friend' : 'friends'
                  } going`
                : `${attendeesTotalCount} going`}
            </div>
          </div>
        )}
        <div className="flex items-center justify-end text-brand-action-lg p-[14px] pt-[7px] pb-[7px] border-t border-dashed border-brand-main text-brand-main">
          <CommentsIcon className="responsive-icon" size={16} />
        </div>
      </a>
    );
  }

  if (isThumbnail) {
    return (
      <a href={href} className="flex flex-row h-[190px] w-full bg-brand-background group">
        <div className="relative w-[190px] h-[190px] flex-shrink-0">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={title}
              className="w-full h-full object-cover object-center"
              loading={priority ? 'eager' : 'lazy'}
            />
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
              <span className="text-gray-400">No Image</span>
            </div>
          )}
        </div>

        <div className="flex flex-col p-4 flex-grow justify-between">
          <div className="flex flex-col justify-between h-full">
            <div>
              <h2
                className={cn(
                  'line-clamp-2 group-hover:text-brand-highlight',
                  'typography-heading-sm text-[18px] md:text-[22px]',
                  bottomClasses,
                )}
              >
                <TWLink to={href}>{title}</TWLink>
              </h2>

              {descriptionLexical ? (
                <div className="mt-2">
                  <BasicEditor
                    initialEditorState={descriptionLexical}
                    isReadOnly={true}
                    withBorder={false}
                    noOfLines={2}
                    contentEditableProps={{
                      className:
                        'text-brand-main text-[16px] !p-0 w-full !border-none line-clamp-2',
                    }}
                    containerProps={{
                      className: 'text-brand-main !py-0 !pr-0',
                    }}
                  />
                </div>
              ) : (
                description && (
                  <div className="typography-action-sm text-brand-main text-[16px] line-clamp-1 mt-2">
                    {description}
                  </div>
                )
              )}
            </div>

            <div>
              {date && (
                <div className="typography-action-sm text-brand-main mt-2">{formattedDate}</div>
              )}

              {location && (
                <div className="flex items-center gap-1 text-brand-main typography-action-sm mt-2 text-[13.5px]">
                  <span className="truncate">{location}</span>
                </div>
              )}
            </div>

            {(attendeesFriendsCount > 0 || attendeesTotalCount > 0) && (
              <div className="flex justify-start">
                <div className="text-brand-highlight underline typography-action-md font-bold">
                  {attendeesFriendsCount > 0
                    ? `${attendeesFriendsCount} ${
                        attendeesFriendsCount === 1 ? 'friend' : 'friends'
                      } going`
                    : `${attendeesTotalCount} going`}
                </div>
              </div>
            )}
          </div>
        </div>
      </a>
    );
  }

  return (
    <a
      href={href}
      className={cn(
        'flex flex-col h-full bg-brand-background group',
        isHeadline ? 'md:min-h-[450px] text-center' : 'text-center md:text-left md:min-h-[350px]',
      )}
    >
      <div className={cn('relative w-full', isHeadline ? 'aspect-[4/5]' : '!aspect-square')}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className={cn(
              'w-full h-full object-cover object-center',
              isHeadline ? 'aspect-[4/5]' : '!aspect-square',
            )}
            loading={priority ? 'eager' : 'lazy'}
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <span className="text-gray-400">No Image</span>
          </div>
        )}
      </div>

      <div className="flex flex-col p-3 flex-grow">
        <h2
          className={cn(
            'mb-2 text-brand-main text-[24px] group-hover:text-brand-highlight',
            titleSize,
            bottomClasses,
            isHeadline ? 'text-center line-clamp-2' : 'text-center md:text-left line-clamp-1',
          )}
        >
          <TWLink to={href}>{title}</TWLink>
        </h2>

        {!hideBottom && (
          <>
            {date && (
              <div className="typography-action-sm text-brand-main mb-1">{formattedDate}</div>
            )}

            {location && (
              <div
                className={cn(
                  'flex items-center gap-1 text-brand-main typography-action-sm mb-2 text-[13.5px]',
                  isHeadline ? 'justify-center' : 'justify-center md:justify-start',
                )}
              >
                <span className="truncate">{location}</span>
              </div>
            )}

            {(attendeesFriendsCount > 0 || attendeesTotalCount > 0) && (
              <div
                className={cn(
                  'flex mt-auto pt-1',
                  isHeadline ? 'justify-center' : 'justify-center md:justify-start',
                )}
              >
                <span className="text-brand-highlight underline">
                  {attendeesFriendsCount > 0
                    ? `${attendeesFriendsCount} ${
                        attendeesFriendsCount === 1 ? 'friend' : 'friends'
                      } going`
                    : `${attendeesTotalCount} going`}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </a>
  );
}